import { FC, useEffect, useState } from "react";
import { TopBar } from "./components/layout/TopBar";
import { Box, Snackbar, useMediaQuery, useTheme } from "@mui/material";
import { Footer } from "./components/layout/Footer";
import {
  MainBannerItemType,
  MainMagicObjType,
  MainPage,
} from "./components/MainPage";
import { UserPage } from "./components/UserPage";
import { errHandling, getFetch, getFetchWithoutToken } from "./utils/http.util";
import { useMeasure } from "./hooks/useMeasure";
import {
  API_ERR_CODE_DISPLAY_ENUM,
  API_ERR_CODE_ENUM,
  API_GET_MAIN_BANNER,
  API_GET_MAIN_MAGIC,
  API_GET_MAIN_MAGIC_SHORTCUT,
  API_USER_INFO,
  ERR_MSG_FAIL_GET_MAIN_BANNER,
  ERR_MSG_FAIL_GET_MAIN_MAGIC,
  ERR_MSG_FAIL_GET_MAIN_MAGIC_SHORTCUT,
  ERR_MSG_FAIL_GET_USER_INFO,
  ERR_MSG_FAIL_REFRESH_USER_INFO,
  ERR_MSG_GET_MAIN_BANNER,
  ERR_MSG_GET_MAIN_MAGIC,
  ERR_MSG_GET_MAIN_MAGIC_SHORTCUT,
  ERR_MSG_GET_USER_INFO,
  ERR_MSG_NOT_LOGIN,
  ERR_MSG_REFRESH_USER_INFO,
  MAGIC_TYPE_ENUM,
} from "./utils/constants";
import { MagicPage } from "./components/MagicPage";
import { MainMagicShortcutType } from "./components/main-page/Shortcuts";
import background from "./images/background.jpg";

type UserInfoType = {
  avatar: string;
  expires_in: string | null;
  nickname: string;
  point: number;
  username: string;
};

// -------------------------------------------------------------------------------------------------------
const App: FC = () => {
  const [userInfo, setUserInfo] = useState<UserInfoType | null>(null);
  const [showMagicPage, setShowMagicPage] = useState(false);
  const [showMainPage, setShowMainPage] = useState(true);
  const [showUserInfo, setShowUserInfo] = useState(false);

  const [contentBoxRef, contentBoxWH] = useMeasure();

  const [snackbarShowing, setSnackbarShowing] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");

  const [magicType, setMagicType] = useState<MAGIC_TYPE_ENUM | null>(null);
  const [magicShortcut, setMagicShortcut] =
    useState<MainMagicShortcutType | null>(null);

  // responsive UI
  const theme = useTheme();
  const notPhone = useMediaQuery(theme.breakpoints.up("sm"));

  const showSnackbar = (promptText: string) => {
    const errMsg = errHandling(promptText);
    setSnackbarMsg(errMsg);
    setSnackbarShowing(true);
  };

  useEffect(() => {
    console.log(" App-useEffect, magicShortcut:", magicShortcut);
  }, [magicShortcut]);

  /* ------------------------------ 登陆后，获取用户信息 ------------------------------------------------------------ */
  const handleGetUserInfo = (data: any) => {
    console.log("-> App-handleGetUserInfo, data:", data);

    if (typeof data?.nickname !== "undefined") {
      setUserInfo(data);
      setShowUserInfo(false);
      setShowMainPage(true);
      setMagicType(null);
      setShowMagicPage(false);
    } else if (data?.errCode === API_ERR_CODE_ENUM.unauthorized) {
      showSnackbar(API_ERR_CODE_DISPLAY_ENUM.unauthorized);
    } else {
      setUserInfo(null);
      setShowMainPage(true);
      setMagicType(null);
      setShowUserInfo(false);
      setShowMagicPage(false);
      showSnackbar(ERR_MSG_FAIL_GET_USER_INFO);
    }
  };

  const handleGetUserInfoErr = (err: any) => {
    console.log("-> App-handleGetUserInfoErr, err:", err);
    showSnackbar(ERR_MSG_GET_USER_INFO);
  };

  useEffect(() => {
    getFetch(API_USER_INFO, handleGetUserInfo, handleGetUserInfoErr, () =>
      showSnackbar(ERR_MSG_NOT_LOGIN)
    );
    // eslint-disable-next-line
  }, []);

  /* ------------------------------ 刷新用户信息 ------------------------------------------------------------ */
  const handleRefreshUserInfo = (data: any) => {
    console.log("-> App-handleRefreshUserInfo, data:", data);

    if (typeof data?.nickname !== "undefined") {
      setUserInfo(data);
    } else if (data?.errCode === API_ERR_CODE_ENUM.unauthorized) {
      showSnackbar(API_ERR_CODE_DISPLAY_ENUM.unauthorized);
    } else {
      showSnackbar(ERR_MSG_FAIL_REFRESH_USER_INFO);
    }
  };

  const handleRefreshUserInfoErr = (err: any) => {
    console.log("-> App-handleRefreshUserInfoErr, err:", err);
    showSnackbar(ERR_MSG_REFRESH_USER_INFO);
  };

  /* ------------------------------ 支付后，刷新用户信息 ------------------------------------------------------------ */
  const handlePaySuccess = () => {
    getFetch(
      API_USER_INFO,
      handleRefreshUserInfo,
      handleRefreshUserInfoErr,
      () => showSnackbar(ERR_MSG_NOT_LOGIN)
    );
  };

  /* ------------------------------ 调用魔法相关API后，刷新用户信息 ------------------------------------------------------------ */
  const handleMagicSuccess = () => {
    getFetch(
      API_USER_INFO,
      handleRefreshUserInfo,
      handleRefreshUserInfoErr,
      () => showSnackbar(ERR_MSG_NOT_LOGIN)
    );
  };

  /* ------------------------------ 获取主页轮播图片/视频 ------------------------------------------------------------ */
  const [mainBannerList, setMainBannerList] = useState<MainBannerItemType[]>(
    []
  );

  const handleGetMainPageBanner = (data: any) => {
    console.log(" -> App-handleGetMainPageBanner, data:", data);

    if (data?.items && data.items?.length > 0) {
      setMainBannerList(data.items);
    } else if (data?.errCode === API_ERR_CODE_ENUM.unauthorized) {
      showSnackbar(API_ERR_CODE_DISPLAY_ENUM.unauthorized);
    } else {
      showSnackbar(ERR_MSG_FAIL_GET_MAIN_BANNER);
    }
  };

  const handleGetMainPageBannerErr = (err: any) => {
    console.log("-> App-handleGetMainPageBannerErr, err:", err);
    showSnackbar(ERR_MSG_GET_MAIN_BANNER);
  };

  useEffect(() => {
    getFetchWithoutToken(
      API_GET_MAIN_BANNER,
      handleGetMainPageBanner,
      handleGetMainPageBannerErr
    );
    // eslint-disable-next-line
  }, []);

  /* ------------------------------ 获取主页展示图片/视频 + 提示词建议 ------------------------------------------------------------ */
  const [mainMagicObj, setMainMagicObj] = useState<MainMagicObjType | null>(
    null
  );

  const handleGetMainPageMagic = (data: any) => {
    console.log("-> App-handleGetMainPageMagic, data:", data);

    if (typeof data?.magic !== "undefined") {
      setMainMagicObj(data.magic);
    } else if (data?.errCode === API_ERR_CODE_ENUM.unauthorized) {
      showSnackbar(API_ERR_CODE_DISPLAY_ENUM.unauthorized);
    } else {
      showSnackbar(ERR_MSG_FAIL_GET_MAIN_MAGIC);
    }
  };

  const handleGetMainPageMagicErr = (err: any) => {
    console.log("-> App-handleGetMainPageMagicErr, err:", err);
    showSnackbar(ERR_MSG_GET_MAIN_MAGIC);
  };

  useEffect(() => {
    getFetchWithoutToken(
      API_GET_MAIN_MAGIC,
      handleGetMainPageMagic,
      handleGetMainPageMagicErr
    );
    // eslint-disable-next-line
  }, []);

  /* ------------------------------ 获取主页 快捷功能 列表 ------------------------------------------------------------ */
  const [mainMagicShortcuts, setMainMagicShortcuts] = useState<
    MainMagicShortcutType[]
  >([]);

  const handleGetMainPageMagicShortcut = (data: any) => {
    console.log("-> App-handleGetMainPageMagicShortcut, data:", data);

    if (data?.shortcuts && data.shortcuts?.length > 0) {
      setMainMagicShortcuts(data.shortcuts);
    } else if (data?.errCode === API_ERR_CODE_ENUM.unauthorized) {
      showSnackbar(API_ERR_CODE_DISPLAY_ENUM.unauthorized);
    } else {
      showSnackbar(ERR_MSG_FAIL_GET_MAIN_MAGIC_SHORTCUT);
    }
  };

  const handleGetMainPageMagicShortcutErr = (err: any) => {
    console.log("-> App-handleGetMainPageMagicShortcutErr, err:", err);
    showSnackbar(ERR_MSG_GET_MAIN_MAGIC_SHORTCUT);
  };

  useEffect(() => {
    getFetchWithoutToken(
      API_GET_MAIN_MAGIC_SHORTCUT,
      handleGetMainPageMagicShortcut,
      handleGetMainPageMagicShortcutErr
    );
    // eslint-disable-next-line
  }, []);

  return (
    <Box
      id="app-box"
      sx={{
        display: "flex",
        flexDirection: "column",
        backgroundImage: `url(${background})`,
        backgroundRepeat: "no-repeat",
      }}
    >
      <TopBar
        userInfo={userInfo}
        onLogoClick={() => {
          setShowMagicPage(false);
          setShowMainPage(true);
          setMagicType(null);
          setShowUserInfo(false);
        }}
        onAvatarClick={() => {
          setShowMagicPage(false);
          setShowMainPage(false);
          if (userInfo) {
            setShowUserInfo(true);
            setMagicType(null);
          } else {
            setShowUserInfo(false);
          }
        }}
        magicType={magicType}
        magicShortcut={magicShortcut}
      />

      <Box
        id="content-box"
        sx={{
          display: "flex",
          flexDirection: "column",
          height: showMainPage || !notPhone ? "100%" : "calc(100vh - 100px)",
          width: "100%",
        }}
        ref={contentBoxRef}
      >
        {showUserInfo && (
          <UserPage
            user={userInfo}
            onPaySuccess={handlePaySuccess}
            showPromptMsg={showSnackbar}
          />
        )}

        {showMainPage && (
          <MainPage
            onMagicGeneratorClick={(type: MAGIC_TYPE_ENUM) => {
              console.log(" -> App-onMagicGeneratorClick, type:", type);
              if (userInfo) {
                setMagicType(type);
                setMagicShortcut(null);
                setShowMainPage(false);
                setShowMagicPage(true);
              } else {
                showSnackbar(ERR_MSG_NOT_LOGIN);
              }
            }}
            bannerList={mainBannerList}
            magicObj={mainMagicObj}
            shortcuts={mainMagicShortcuts}
            onMagicShortcutStartClick={() => {
              if (userInfo) {
                setShowMainPage(false);
                setShowMagicPage(true);
              } else {
                showSnackbar(ERR_MSG_NOT_LOGIN);
              }
            }}
            showPromptMsg={showSnackbar}
            onMagicShortcutModalOpenChange={(
              shortcut: MainMagicShortcutType | null
            ) => {
              console.log(
                " -> App-onMagicShortcutModalOpenChange, shortcut:",
                shortcut
              );
              if (shortcut) {
                setMagicType(shortcut.params.magic_type);
                setMagicShortcut(shortcut);
              } else {
                setMagicType(null);
                setMagicShortcut(null);
              }
            }}
          />
        )}

        {showMagicPage && (
          <MagicPage
            parentHeight={contentBoxWH.height}
            onMagicSuccess={handleMagicSuccess}
            magicType={magicType}
            magicObj={mainMagicObj}
            showPromptMsg={showSnackbar}
            magicShortcutObj={magicShortcut}
          />
        )}
      </Box>

      <Footer
        inMainPage={showMainPage}
        inMagicShortcutFunc={Boolean(magicShortcut)}
      />

      <Snackbar
        open={snackbarShowing}
        autoHideDuration={6000}
        onClose={() => setSnackbarShowing(false)}
        message={snackbarMsg}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
};

export default App;
